import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import {
  fetchPlayerDispositions,
  PlayerDispositionGenderFilter,
} from '@/services/reports/reportService';
import { PlayerDispositionRow } from '@/types/reports';
import { formatDateString } from '@/lib/utils';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

type SortField =
  | 'playerName'
  | 'sex'
  | 'birthdate'
  | 'ageGroup'
  | 'evaluations';
type SortDirection = 'asc' | 'desc';

function genderLabel(sex: string): string {
  if (sex === 'male') return 'Boys';
  if (sex === 'female') return 'Girls';
  return sex;
}

function formatTeams(teams: PlayerDispositionRow['teams']): string {
  return teams.map((t) => `${t.team_name} (${t.status})`).join(', ');
}

function formatTeamsCsv(teams: PlayerDispositionRow['teams']): string {
  return teams.map((t) => `${t.team_name} (${t.status})`).join('; ');
}

function isContested(row: PlayerDispositionRow): boolean {
  const rosteredTeamIds = new Set(
    row.teams.filter((t) => t.status === 'rostered').map((t) => t.team_id)
  );
  if (rosteredTeamIds.size !== 1) return false;
  return row.teams.some(
    (t) =>
      (t.status === 'considering' || t.status === 'offered') &&
      !rosteredTeamIds.has(t.team_id)
  );
}

export default function PlayerDispositionReport() {
  const [rows, setRows] = useState<PlayerDispositionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [genderFilter, setGenderFilter] =
    useState<PlayerDispositionGenderFilter>('all');

  const [sortField, setSortField] = useState<SortField>('birthdate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [contestedOnly, setContestedOnly] = useState(false);

  async function fetchReportData() {
    setLoading(true);
    setError(null);
    setReportGenerated(true);
    setRows([]);
    try {
      const data = await fetchPlayerDispositions(genderFilter);
      setRows(data);
    } catch (err: any) {
      console.error('Error fetching player dispositions:', err);
      setError(err?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedRows = (): PlayerDispositionRow[] => {
    const filtered = contestedOnly ? rows.filter(isContested) : rows;
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'playerName': {
          const an = `${a.last_name}, ${a.first_name}`;
          const bn = `${b.last_name}, ${b.first_name}`;
          comparison = an.localeCompare(bn);
          break;
        }
        case 'sex':
          comparison = genderLabel(a.sex).localeCompare(genderLabel(b.sex));
          break;
        case 'birthdate': {
          const at = a.birthdate ? new Date(a.birthdate).getTime() : Infinity;
          const bt = b.birthdate ? new Date(b.birthdate).getTime() : Infinity;
          comparison = at - bt;
          break;
        }
        case 'ageGroup':
          comparison = a.age_group_label.localeCompare(b.age_group_label);
          break;
        case 'evaluations':
          comparison = a.evaluations_attended - b.evaluations_attended;
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const exportCSV = () => {
    const sorted = getSortedRows();
    if (sorted.length === 0) return;
    const headers = [
      'Player Name',
      'Gender',
      'Birthdate',
      'Age Group',
      'Evaluations Attended',
      'Team Assignments',
    ];
    const csvContent = [
      headers.join(','),
      ...sorted.map((r) =>
        [
          `"${r.first_name} ${r.last_name}"`,
          `"${genderLabel(r.sex)}"`,
          `"${r.birthdate ? formatDateString(r.birthdate, 'en-US', 'short') : ''}"`,
          `"${r.age_group_label}"`,
          r.evaluations_attended,
          `"${formatTeamsCsv(r.teams)}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `player_disposition_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field)
      return (
        <ChevronDown className="ml-1 h-4 w-4 text-gray-400 opacity-50 inline" />
      );
    return sortDirection === 'asc' ? (
      <ChevronUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ChevronDown className="ml-1 h-4 w-4 inline" />
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Player Disposition</CardTitle>
        <CardDescription>
          For every registered player: number of evaluation sessions checked
          into and current team assignments with status.
        </CardDescription>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3">
          <div
            className="flex items-center gap-2"
            role="radiogroup"
            aria-label="Filter by gender"
          >
            <span className="text-sm font-medium text-gray-700">Gender:</span>
            {(['all', 'male', 'female'] as PlayerDispositionGenderFilter[]).map(
              (value) => {
                const label =
                  value === 'all'
                    ? 'All'
                    : value === 'male'
                      ? 'Boys'
                      : 'Girls';
                const active = genderFilter === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setGenderFilter(value)}
                    className={`px-3 py-1 text-sm rounded border ${
                      active
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                );
              }
            )}
          </div>
          <div className="flex space-x-2">
            <Button onClick={fetchReportData} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...
                </>
              ) : (
                'Generate Report'
              )}
            </Button>
            {reportGenerated && rows.length > 0 && (
              <Button
                variant="outline"
                onClick={exportCSV}
                disabled={loading || getSortedRows().length === 0}
              >
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
            )}
          </div>
        </div>
        {reportGenerated && rows.length > 0 && (
          <div className="pt-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={contestedOnly}
                onChange={(e) => setContestedOnly(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show only players rostered on one team and considered or offered
              by another
            </label>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <p className="ml-2 text-gray-600">Loading player dispositions...</p>
          </div>
        )}
        {error && !loading && (
          <Alert variant="destructive" className="my-4">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load player dispositions: {error}
            </AlertDescription>
          </Alert>
        )}
        {reportGenerated &&
          !loading &&
          !error &&
          rows.length > 0 &&
          getSortedRows().length === 0 && (
            <Alert className="my-4">
              <AlertTitle>No Data</AlertTitle>
              <AlertDescription>
                No players are rostered on one team while also being considered
                or offered on another.
              </AlertDescription>
            </Alert>
          )}
        {reportGenerated && !loading && !error && rows.length === 0 && (
          <Alert className="my-4">
            <AlertTitle>No Data</AlertTitle>
            <AlertDescription>
              No players match the selected filter.
            </AlertDescription>
          </Alert>
        )}
        {reportGenerated && !loading && !error && getSortedRows().length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('playerName')}
                >
                  Player Name <SortIcon field="playerName" />
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('sex')}
                >
                  Gender <SortIcon field="sex" />
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('birthdate')}
                >
                  Birthdate <SortIcon field="birthdate" />
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('ageGroup')}
                >
                  Age Group <SortIcon field="ageGroup" />
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50 text-right"
                  onClick={() => handleSort('evaluations')}
                >
                  Evaluations Attended <SortIcon field="evaluations" />
                </TableHead>
                <TableHead>Team Assignments</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getSortedRows().map((r) => (
                <TableRow key={r.player_id}>
                  <TableCell>
                    <Link
                      to={`/player/${r.player_id}/edit`}
                      className="text-blue-600 hover:underline"
                    >
                      {r.first_name} {r.last_name}
                    </Link>
                  </TableCell>
                  <TableCell>{genderLabel(r.sex)}</TableCell>
                  <TableCell>
                    {r.birthdate
                      ? formatDateString(r.birthdate, 'en-US', 'short')
                      : '—'}
                  </TableCell>
                  <TableCell>{r.age_group_label}</TableCell>
                  <TableCell className="text-right">
                    {r.evaluations_attended}
                  </TableCell>
                  <TableCell className="text-gray-700">
                    {r.teams.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      formatTeams(r.teams)
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
